import { useCallback, useState } from "react";
import { useApolloClient } from "@apollo/client";
import { message } from "antd";

import { localizeIt } from "components/service/localize";
import { useNotification } from "components/use-hooks";

import CommentsHelpers from "../helpers";

const initialModalState = {
    open: false,
    mode: "add",
    sessionId: null,
    commentId: null,
    draftCommentId: null,
    text: "",
    initialText: "",
    translationRequired: false,
    translationReady: true,
    translationStatus: "idle",
    translatedFromText: "",
    translationSubmitText: "",
    translationDirty: false,
    translationShouldBlockSubmit: false,
    attachments: [],
    loading: false
};

const useCommentModal = ({ standId, standEeId, api, draftSession, comments }) => {
    const client = useApolloClient();
    const { nError, nSuccess } = useNotification();

    const [ modalState, setModalState ] = useState(initialModalState);

    const closeModal = () => {
        setModalState(initialModalState);
    };

    const silentDeleteAttachment = async (id) => {
        if (!id) return;
        await api.deleteAttachment({ variables: { id: Number(id) } }).catch(() => {});
    };

    const silentDeleteComment = async (id) => {
        if (!id) return;
        await api.deleteComment({ variables: { id: Number(id) } }).catch(() => {});
    };

    const cleanupAddSession = async ({ sessionId, draftCommentId }) => {
        if (!sessionId) return;

        const attachmentIds = draftSession.getAttachmentIds(sessionId);

        if (!draftCommentId) {
            draftSession.markShouldCleanupDraft(sessionId);
            return;
        }

        if (attachmentIds.length) {
            await Promise.allSettled(attachmentIds.map((id) => silentDeleteAttachment(id)));
        }

        await silentDeleteComment(draftCommentId);
        draftSession.clearSession(sessionId);
    };

    const handleOpenAdd = async () => {
        const sessionId = draftSession.startAddSession();
        setModalState({ ...initialModalState, open: true, mode: "add", sessionId });

        const result = await api.createComment({
            variables: { input: { model: "Stand", model_id: standId, content: "" } }
        }).catch((error) => {
            nError(error);
            return null;
        });

        const createdId = Number(result?.data?.commentCreate?.comment?.id);

        if (!createdId) {
            closeModal();
            return;
        }

        const draftCommentId = draftSession.registerDraft(sessionId, createdId);

        if (draftSession.shouldCleanupDraftAfterCreate(sessionId)) {
            await cleanupAddSession({ sessionId, draftCommentId });
            closeModal();
            return;
        }

        setModalState((prev) => {
            if (prev.sessionId !== sessionId) return prev;
            return { ...prev, draftCommentId };
        });
    };

    const handleOpenEdit = async (commentId) => {
        const normalizedCommentId = Number(commentId);
        const initialComment = comments.find((item) => Number(item.id) === normalizedCommentId);

        setModalState({
            ...initialModalState,
            open: true,
            mode: "edit",
            commentId: normalizedCommentId,
            text: initialComment?.text || "",
            initialText: initialComment?.text || "",
            translationSubmitText: initialComment?.text || "",
            attachments: initialComment?.attachments || [],
            loading: !initialComment
        });

        const result = await api.getComment({ variables: { id: normalizedCommentId } })
            .catch((error) => {
                nError(error);
                return null;
            });

        const comment = result?.data?.comment;
        if (!comment) {
            if (!initialComment) closeModal();
            return;
        }

        const mapped = CommentsHelpers.Mappers.mapComment(comment);
        setModalState((prev) => {
            if (prev.mode !== "edit" || Number(prev.commentId) !== normalizedCommentId) return prev;
            return {
                ...prev,
                text: mapped.text,
                initialText: mapped.text,
                translationSubmitText: mapped.text,
                attachments: mapped.attachments,
                loading: false
            };
        });
    };

    const handleCloseModal = async () => {
        if (modalState.mode === "add") {
            await cleanupAddSession({
                sessionId: modalState.sessionId,
                draftCommentId: modalState.draftCommentId || draftSession.getDraftId(modalState.sessionId)
            });
        }
        closeModal();
    };

    const handleTextChange = (value) => {
        setModalState((prev) => ({ ...prev, text: value }));
    };

    const handleTranslationStateChange = useCallback(({
            required,
            ready,
            status,
            translatedFromText,
            submitText,
            dirty,
            shouldBlockSubmit
        }) => {
        setModalState((prev) => {
            if (
                prev.translationRequired === required &&
                prev.translationReady === ready &&
                prev.translationStatus === status &&
                prev.translatedFromText === translatedFromText &&
                prev.translationSubmitText === submitText &&
                prev.translationDirty === dirty &&
                prev.translationShouldBlockSubmit === shouldBlockSubmit
            ) {
                return prev;
            }

            return {
                ...prev,
                translationRequired: required,
                translationReady: ready,
                translationStatus: status,
                translatedFromText,
                translationSubmitText: submitText,
                translationDirty: dirty,
                translationShouldBlockSubmit: shouldBlockSubmit
            };
        });
    }, []);

    const handleAttachmentUpload = async ({ file }) => {
        const canUpload = modalState.attachments.length < 3;
        const targetCommentId = modalState.mode === "add" ? modalState.draftCommentId : modalState.commentId;

        if (!canUpload || !targetCommentId || !standEeId) return false;

        const result = await api.createAttachment({
            variables: {
                ee_id: Number(standEeId),
                file,
                model: "Comment",
                model_id: Number(targetCommentId)
            }
        }).catch((error) => {
            nError(error);
            return null;
        });

        const attachment = result?.data?.attachmentCreate?.attachment;
        if (!attachment) return false;

        const mappedAttachment = CommentsHelpers.Mappers.mapAttachment(attachment);

        setModalState((prev) => ({
            ...prev,
            attachments: [ ...prev.attachments, mappedAttachment ]
        }));

        if (modalState.mode === "add" && modalState.sessionId) {
            draftSession.addAttachment(modalState.sessionId, mappedAttachment.id);
        }

        return false;
    };

    const handleAttachmentDelete = async (attachmentId) => {
        if (!attachmentId) return;

        const result = await api.deleteAttachment({ variables: { id: Number(attachmentId) } })
            .catch((error) => {
                nError(error);
                return null;
            });

        if (result?.data?.attachmentDelete) {
            setModalState((prev) => ({
                ...prev,
                attachments: prev.attachments.filter(({ id }) => id !== Number(attachmentId))
            }));

            if (modalState.mode === "add" && modalState.sessionId) {
                draftSession.removeAttachment(modalState.sessionId, Number(attachmentId));
            }
        }
    };

    const updateStandCount = (delta) => {
        client.cache.modify({
            id: client.cache.identify({ __typename: "Stand", id: standId }),
            fields: { comments_count: (existing = 0) => Math.max(0, existing + delta) }
        });
    };

    const handleSubmitModal = async () => {
        const isAddMode = modalState.mode === "add";
        const targetCommentId = isAddMode ? modalState.draftCommentId : modalState.commentId;

        if (!targetCommentId) {
            message.error(localizeIt("STAND.Text_CommentDraftError"));
            return;
        }

        if (
            String(modalState.text || "").trim() &&
            modalState.translationShouldBlockSubmit
        ) {
            message.error(localizeIt("TRANSLATION.Text_InputTranslationRequired"));
            return;
        }

        const submitText = typeof modalState.translationSubmitText === "string" ?
            modalState.translationSubmitText :
            (modalState.text || "");

        const result = await api.updateComment({
            variables: {
                input: {
                    id: Number(targetCommentId),
                    model: "Stand",
                    model_id: standId,
                    content: submitText
                }
            }
        }).catch((error) => {
            nError(error);
            return null;
        });

        const payload = result?.data?.commentUpdate;
        if (!payload) return;

        nSuccess(payload.label, payload.message);

        if (isAddMode) {
            if (modalState.sessionId) draftSession.clearSession(modalState.sessionId);
            updateStandCount(1);
        }

        await api.commentsQuery.refetch();
        closeModal();
    };

    const handleDeleteComment = async (commentId) => {
        const result = await api.deleteComment({ variables: { id: Number(commentId) } })
            .catch((error) => {
                nError(error);
                return null;
            });

        const payload = result?.data?.commentDelete;
        if (!payload) return;

        nSuccess(payload.label, payload.message);
        updateStandCount(-1);

        await api.commentsQuery.refetch();
    };

    const isDraftCreating = (
        modalState.mode === "add" &&
        !modalState.draftCommentId &&
        api.createCommentResult.loading
    );

    return {
        modalState,
        handleOpenAdd,
        handleOpenEdit,
        handleCloseModal,
        handleTextChange,
        handleTranslationStateChange,
        handleAttachmentUpload,
        handleAttachmentDelete,
        handleSubmitModal,
        handleDeleteComment,
        isDraftCreating,
        loadingAttachmentCreate: api.createAttachmentResult.loading
    };
};

export default useCommentModal;
